import type {
  GetListRequestV2,
  GetListResponseOfProjectRosterRowResponse,
  GetListResponseOfProjectSimpleResponse,
  GetListResponseOfSelectOption,
  GetProjectListRequest,
  GetProjectListRequestV2,
  ProjectRequest,
  ProjectResponse,
} from 'src/api/ProactApiClient';
import {projectApiClient} from 'src/api/ProactApiClientInstances';
import {projectSelectOptionsQueryFn} from 'src/app/tasks/offline/reads';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

export const projectApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getProjectSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      GetProjectListRequest
    >({
      queryFn: projectSelectOptionsQueryFn(params =>
        projectApiClient.listSelectOptions(params)
      ),
      providesTags: ['Projects'],
    }),
    getProjectList: builder.query<
      GetListResponseOfProjectSimpleResponse,
      GetProjectListRequest
    >({
      queryFn: clientQuery(params => projectApiClient.list(params)),
      providesTags: ['Projects'],
    }),
    getProjectListV2: builder.query<
      GetListResponseOfProjectSimpleResponse,
      GetProjectListRequestV2
    >({
      queryFn: clientQuery(params => projectApiClient.listV2(params)),
      providesTags: ['Projects'],
    }),
    getProject: builder.query<ProjectResponse, number>({
      queryFn: clientQuery(id => projectApiClient.get(id)),
      providesTags: ['Projects'],
    }),
    getProjectRosterListV2: builder.query<
      GetListResponseOfProjectRosterRowResponse,
      {projectId: number; request: GetListRequestV2}
    >({
      queryFn: clientQuery(async ({projectId, request}) => {
        const response = await projectApiClient.getRosterList(
          projectId,
          request
        );
        return {
          ...response,
          data: response.data?.map(row => ({
            ...row,
            userName: row.userName ?? undefined,
            departmentId: row.departmentId ?? undefined,
            departmentName: row.departmentName ?? undefined,
            vendorId: row.vendorId ?? undefined,
            vendorName: row.vendorName ?? undefined,
          })),
        };
      }),
      providesTags: ['ProjectRoster'],
    }),
    createProject: builder.mutation<ProjectResponse, ProjectRequest>({
      queryFn: clientQuery(request => projectApiClient.create(request)),
      invalidatesTags: ['Projects'],
    }),
    updateProject: builder.mutation<ProjectResponse, ProjectRequest>({
      queryFn: clientQuery(request => projectApiClient.update(request)),
      invalidatesTags: ['Projects'],
    }),
  }),
});

export const {
  useGetProjectListQuery,
  useGetProjectSelectOptionsQuery,
  useGetProjectListV2Query,
  useGetProjectQuery,
  useGetProjectRosterListV2Query,
  useCreateProjectMutation,
  useUpdateProjectMutation,
} = projectApi;
